import athenaConfig from '../../config';
import { passwordResetClassic, shouldPromptForSymantecVIP } from '../../utils/anet-client';
import { getOktaUserId, getOktaWidgetModel, getStateToken, showFormErrors, startSpinnerAndHideForm, stopSpinner, stopSpinnerAndShowForm } from '../../utils/widget-facade';
import PasswordResetBaseCustomizer, { PasswordRequirementsSelector } from './base';
import { getAnetUsername, isAnetUser, isProdUserInSupport } from '../../utils/user-utils';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { setSecurityQuestionsPromptCookies } from '../../utils/storage-utils';
import PasswordRequirements from '../../components/password-reset/PasswordRequirements';

const { classic } = athenaConfig;

interface SymantecPromptPayloadClassic {
  username: string;
  stateToken: string;
}

export default class PasswordResetClassicCustomizer extends PasswordResetBaseCustomizer<SymantecPromptPayloadClassic> {
  static manageFactorValue: string | null = null;
  static clientId: string | null = null;

  constructor(args: any) {
    super(args);
    this.symantecFieldIndex = 1;
  }

  protected getSymantecCodeTarget(): JQuery<HTMLElement> {
    return jQueryCourage('#athena-oldPassword-input-field-container');
  }

  protected customizePasswordResetButton() {
    // Monkey patch model's validate method and add on the Symantec code's validation.
    // This lets us keep using Okta's validations out of the box for rest of the fields
    // For classic - https://github.com/okta/okta-signin-widget/blob/7.8/src/v1/controllers/PasswordExpiredController.js#L31
    // For OIE - https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/view-builder/views/password/EnrollAuthenticatorPasswordView.js#L86
    const model = getOktaWidgetModel();
    const originalValidate = model.validate.bind(model);
    model.validate = () => {
      const response = originalValidate();
      const symantecCodeValid = this.isSecurityCodeValid();
      if (response) {
        return response;
      }
      else {
        // return a hash when symantec token is not valid so widget set the top error banner
        return symantecCodeValid ? undefined : { 'symantec-token': 'error' };
      }
    };
    this.onPasswordReset();
  }

  protected onPasswordReset() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    // Monkey patch the changePassword method in PasswordExpiredController to hit aNet APIs instead of Okta's
    // https://github.com/okta/okta-signin-widget/blob/7.8/src/v1/controllers/PasswordExpiredController.js#L36-L39
    const model = getOktaWidgetModel();
    const transaction = model.appState.get('transaction');
    transaction.changePassword = async function (payload: { newPassword: string; oldPassword: string }) {
      try {
        AmplitudeUtils.logPasswordResetEvent('PasswordChangeInitiated', {
          shouldPromptForSymantec: self.shouldPromptForSymantec,
        });
        const response = await self.triggerPasswordResetRequest({
          symantecSecurityCode: self.getSymantecSecurityCode(),
          ...payload,
        });
        AmplitudeUtils.logPasswordResetEvent('PasswordChangeSuccess', {
          shouldPromptForSymantec: self.shouldPromptForSymantec,
        });
        if (response.sqtoken) {
          setSecurityQuestionsPromptCookies({
            sqtoken: response.sqtoken,
            primaryAuthWithCustomTxn: true,
          });
        }
        return global.oktaSignIn.authClient.tx.createTransaction(response);
      }
      catch (err: any) {
        AmplitudeUtils.logPasswordResetEvent('PasswordChangeFailed', {
          errorMessage: err.errorSummary ?? err.message,
          shouldPromptForSymantec: self.shouldPromptForSymantec,
        });
        const { message } = err;
        if (self.isSymantecValidationError(message)) {
          self.clearPasswordResetForm();
        }
        else {
          if (self.shouldPromptForSymantec) {
            self.clearSymantecCode();
          }
        }
        throw err;
      }
    };
  }

  protected forgifyPasswordRequirements() {
    const { headerSelector, itemListSelector, containerSelector } = this.getPasswordRequirementsSelector();
    const passwordRequirements: JQuery = jQueryCourage(containerSelector);
    const component = (
      <PasswordRequirements
        passwordRequirements={passwordRequirements}
        headerSelector={headerSelector}
        itemListSelector={itemListSelector}
      />
    );

    this.renderForgeAndHideOktaComponent({
      id: passwordRequirements.attr('data-se')!,
      forgeComponent: component,
      oktaComponent: passwordRequirements,
    });
  }

  protected async triggerPasswordResetRequest({ newPassword, oldPassword, symantecSecurityCode }: any): Promise<any> {
    const symantecPromptPayload = await this.getSymantecPromptPayload();
    return passwordResetClassic({
      newPassword,
      oldPassword,
      symantecSecurityCode,
      ...symantecPromptPayload,
    });
  }

  async shouldCustomize() {
    return (await super.shouldCustomize()) && classic && isAnetUser();
  }

  protected manageFactorEvent = (e: CustomEvent) => {
    PasswordResetClassicCustomizer.manageFactorValue = e.detail.manageFactorValue;
    PasswordResetClassicCustomizer.clientId = e.detail.clientId;
  };

  protected async getSymantecPromptPayload(): Promise<SymantecPromptPayloadClassic> {
    return {
      username: getAnetUsername(),
      stateToken: getStateToken(),
    };
  }

  protected async shouldPromptForSymantecVIP(payload: any) {
    return shouldPromptForSymantecVIP(payload);
  }

  protected async handleSymantecPromptError(error: any) {
    const errorMessage = error.errorSummary || error.message;
    AmplitudeUtils.logPasswordResetEvent('SymantecVipPromptFailure', {
      errorMessage,
    });
    showFormErrors(error);
    stopSpinner();
  }

  protected async handleSymantecPrompt() {
    const checkPayload = await this.getSymantecPromptPayload();
    this.shouldPromptForSymantec = await this.shouldPromptForSymantecVIP(checkPayload);
    if (this.shouldPromptForSymantec) {
      this.customizeForEpcs();
      AmplitudeUtils.logPasswordResetEvent('SymantecVipPrompted', {
        userId: getOktaUserId(),
      });
    }
  }

  protected getPasswordRequirementsSelector(): PasswordRequirementsSelector {
    return {
      headerSelector: '.password-requirements--header',
      itemListSelector: '.password-requirements--list',
      containerSelector: 'div[data-se="password-requirements-html"]',
    };
  }

  async customize() {
    try {
      startSpinnerAndHideForm();
      const prodUserInSupport = isProdUserInSupport();
      const skipLink = jQueryCourage('.auth-footer a[data-se="skip-link"]');
      const isPasswordWarningScreen = skipLink.length > 0;
      if (prodUserInSupport && isPasswordWarningScreen) {
        skipLink.trigger('click');
        stopSpinner();
        return;
      }
      await super.customize();
      if (prodUserInSupport) {
        this.blockPasswordUpdateForProdUser();
        return;
      }
      await this.handleSymantecPrompt();
      stopSpinnerAndShowForm();
    }
    catch (error: any) {
      await this.handleSymantecPromptError(error);
    }
  }
}

